<main class="grow" role="content">
  <div class="kt-container-fixed bond-definition-container py-5 lg:py-7.5">
    <div class="kt-card">
      <div class="kt-card-header min-h-16 flex-wrap gap-3">
        <div class="flex min-w-0 flex-col gap-1">
          <div class="text-xs text-secondary-foreground">Bonds / Definition</div>
          <h1 class="kt-card-title">Bond Definition</h1>
        </div>

        <div class="inline-flex items-center gap-2.5 flex-wrap ms-auto">
          <span class="kt-badge kt-badge-outline">
            Last Modified By: {{ detail()?.modifiedBy || '' }}
            @if (detail()?.dateModified) {
              &nbsp;|&nbsp;{{ formatDateTimeOrBlank(detail()?.dateModified || '') }}
            }
          </span>

          <button
            type="button"
            class="kt-btn kt-btn-sm kt-btn-light"
            (click)="saveDefinition()"
            [disabled]="!hasUnsavedChanges() || isSaving()"
          >
            @if (!isSaving()) {
              Save
            } @else {
              Saving...
            }
          </button>
        </div>
      </div>

      <div class="kt-card-content bond-definition-content-shell p-5 lg:p-7.5">
        @if (isLoading()) {
          <section class="kt-card bond-definition-readonly-card">
            <div class="kt-card-content p-6">
              <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading bond definition">
                <span class="catalog-loading-spinner" aria-hidden="true"></span>
                <span class="catalog-loading-text">Loading...</span>
              </div>
            </div>
          </section>
        }

        @if (errorMessage()) {
          <section class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
            <span>{{ errorMessage() }}</span>
            <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="onRetry()">Retry</button>
          </section>
        }

        @if (saveError()) {
          <section class="kt-alert kt-alert-destructive">
            <span>{{ saveError() }}</span>
          </section>
        }

        @if (!isLoading() && !errorMessage() && detail(); as detailData) {
          <section class="kt-card bond-definition-readonly-card">
            <div class="kt-card-content p-5">
              <div class="bond-definition-readonly-toolbar">
                <div class="bond-definition-readonly-grid">
                  <div class="bond-definition-readonly-item">
                    <span class="bond-definition-readonly-label">Security ID</span>
                    <span class="bond-definition-readonly-value">{{ detailData.secId || '-' }}</span>
                  </div>
                  <div class="bond-definition-readonly-item">
                    <span class="bond-definition-readonly-label">CUSIP</span>
                    <span class="bond-definition-readonly-value">{{ detailData.cusip || '-' }}</span>
                  </div>
                  <div class="bond-definition-readonly-item">
                    <span class="bond-definition-readonly-label">Coupon - Mat. Date</span>
                    <span class="bond-definition-readonly-value">{{ couponMaturityValue() }}</span>
                  </div>
                  <div class="bond-definition-readonly-item">
                    <span class="bond-definition-readonly-label">ISIN</span>
                    <span class="bond-definition-readonly-value">{{ detailData.isin || '-' }}</span>
                  </div>
                  <div class="bond-definition-readonly-item">
                    <span class="bond-definition-readonly-label">TMS User</span>
                    <span class="bond-definition-readonly-value">{{ detailData.tmsUser || '-' }}</span>
                  </div>
                </div>

                <button
                  type="button"
                  class="kt-btn kt-btn-sm kt-btn-outline"
                  [disabled]="!hasFormulas()"
                  (click)="openFormulasModal()"
                >
                  <i class="ki-filled ki-chart-line" aria-hidden="true"></i>
                  <span>Formulas</span>
                </button>
              </div>
            </div>
          </section>

          @if (detailData.isNew) {
            <section class="kt-card bond-definition-gate-card">
              <div class="kt-card-content p-6">
                <h2 class="text-base font-semibold">Initialize this bond definition in Frida</h2>
                <p class="mt-2 text-sm text-secondary-foreground">
                  You are about to register information about this bond in FRIDA's database. Do you want to proceed?
                </p>

                @if (initializeError()) {
                  <div class="kt-alert kt-alert-destructive mt-4">
                    {{ initializeError() }}
                  </div>
                }

                <div class="mt-5">
                  <button
                    type="button"
                    class="kt-btn kt-btn-primary"
                    (click)="onProceedWithNewBond()"
                    [disabled]="isInitializing()"
                  >
                    @if (!isInitializing()) {
                      Proceed
                    } @else {
                      Processing...
                    }
                  </button>
                </div>
              </div>
            </section>
          } @else {
            <section class="kt-card bond-definition-tabs-card">
              <div class="kt-card-header min-h-14">
                <div class="kt-tabs kt-tabs-line bond-definition-tabs" role="tablist" aria-label="Bond definition tabs">
                  <button
                    type="button"
                    role="tab"
                    class="kt-tab-toggle"
                    [class.active]="isTabActive('definition')"
                    [class.kt-tab-active]="isTabActive('definition')"
                    [attr.aria-selected]="isTabActive('definition')"
                    (click)="setActiveTab('definition')"
                  >
                    <i class="ki-filled ki-setting-2" aria-hidden="true"></i>
                    Definition
                  </button>

                  <button
                    type="button"
                    role="tab"
                    class="kt-tab-toggle"
                    [class.active]="isTabActive('documentation')"
                    [class.kt-tab-active]="isTabActive('documentation')"
                    [attr.aria-selected]="isTabActive('documentation')"
                    (click)="setActiveTab('documentation')"
                  >
                    <i class="ki-filled ki-file-up" aria-hidden="true"></i>
                    Documentation
                  </button>

                  <button
                    type="button"
                    role="tab"
                    class="kt-tab-toggle"
                    [class.active]="isTabActive('notes')"
                    [class.kt-tab-active]="isTabActive('notes')"
                    [attr.aria-selected]="isTabActive('notes')"
                    (click)="setActiveTab('notes')"
                  >
                    <i class="ki-filled ki-notepad" aria-hidden="true"></i>
                    Notes
                  </button>
                </div>
              </div>

              <div class="kt-card-content p-5">
                @if (isTabActive('definition')) {
                  <section class="bond-definition-tab-panel">
                    <div class="bond-definition-section-grid">
                      <div class="bond-form-field">
                        <label class="kt-form-label" for="bondDefinitionDocumentationId">Documentation</label>
                        <select
                          id="bondDefinitionDocumentationId"
                          class="kt-select"
                          [value]="toSelectValue(definitionForm().bondDocumentationId)"
                          (change)="onDefinitionSelectChange('bondDocumentationId', $any($event.target).value)"
                        >
                          <option value="">-</option>
                          @for (option of detailData.bondDocumentations; track option.id) {
                            <option [value]="option.id" [selected]="isSelectOptionSelected(definitionForm().bondDocumentationId, option.id)">
                              {{ option.name }}
                            </option>
                          }
                        </select>
                        @if (shouldShowDocumentationDefaultWarning()) {
                          <span class="bond-field-hint bond-field-hint-warning">
                            Documentation default value selected but not yet stored in Database.
                          </span>
                        }
                      </div>

                      <div class="bond-form-field">
                        <label class="kt-form-label" for="bondDefinitionPricingSupplement">Pricing Supplement - Series Number</label>
                        <input
                          id="bondDefinitionPricingSupplement"
                          class="kt-input"
                          type="text"
                          [value]="definitionForm().bondPricingSupplement"
                          (input)="onDefinitionTextChange('bondPricingSupplement', $any($event.target).value)"
                        />
                      </div>

                      <div class="bond-form-field">
                        <label class="kt-form-label" for="bondDefinitionFormatId">Format</label>
                        <select
                          id="bondDefinitionFormatId"
                          class="kt-select"
                          [value]="toSelectValue(definitionForm().bondFormatId)"
                          (change)="onDefinitionSelectChange('bondFormatId', $any($event.target).value)"
                        >
                          <option value="">-</option>
                          @for (option of detailData.bondFormatTypes; track option.id) {
                            <option [value]="option.id" [selected]="isSelectOptionSelected(definitionForm().bondFormatId, option.id)">
                              {{ option.format }}
                            </option>
                          }
                        </select>
                      </div>

                      <div class="bond-form-field">
                        <label class="kt-form-label" for="bondDefinitionListingId">Listing</label>
                        <select
                          id="bondDefinitionListingId"
                          class="kt-select"
                          [value]="toSelectValue(definitionForm().bondListingId)"
                          (change)="onDefinitionSelectChange('bondListingId', $any($event.target).value)"
                        >
                          <option value="">-</option>
                          @for (option of detailData.bondListings; track option.id) {
                            <option [value]="option.id" [selected]="isSelectOptionSelected(definitionForm().bondListingId, option.id)">
                              {{ option.name }}
                            </option>
                          }
                        </select>
                      </div>

                      <div class="bond-form-field">
                        <label class="kt-form-label" for="bondDefinitionProceedId">Proceed Type</label>
                        <select
                          id="bondDefinitionProceedId"
                          class="kt-select"
                          [value]="toSelectValue(definitionForm().bondProceedId)"
                          (change)="onDefinitionSelectChange('bondProceedId', $any($event.target).value)"
                        >
                          <option value="">-</option>
                          @for (option of detailData.bondProceeds; track option.id) {
                            <option [value]="option.id" [selected]="isSelectOptionSelected(definitionForm().bondProceedId, option.id)">
                              {{ option.name }}
                            </option>
                          }
                        </select>
                      </div>

                      <div class="bond-form-field">
                        <label class="kt-form-label" for="bondDefinitionProceedSubtypeId">Proceed Subtype</label>
                        <select
                          id="bondDefinitionProceedSubtypeId"
                          class="kt-select"
                          [value]="toSelectValue(definitionForm().bondProceedSubtypeId)"
                          (change)="onDefinitionSelectChange('bondProceedSubtypeId', $any($event.target).value)"
                        >
                          <option value="">-</option>
                          @for (option of filteredProceedSubtypes(); track option.id) {
                            <option [value]="option.id" [selected]="isSelectOptionSelected(definitionForm().bondProceedSubtypeId, option.id)">
                              {{ option.name }}
                            </option>
                          }
                        </select>
                      </div>
                    </div>
                  </section>
                }

                @if (isTabActive('documentation')) {
                  <section class="bond-definition-tab-panel">
                    <div class="bond-tab-stack">
                      <div class="flex items-center justify-between gap-2 flex-wrap">
                        <h2 class="text-base font-semibold">Attached Documentation</h2>
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-outline"
                          [disabled]="isMutatingDocument()"
                          data-kt-modal-toggle="#bond_definition_add_document_modal"
                          (click)="resetAddDocumentForm()"
                        >
                          <i class="ki-filled ki-plus" aria-hidden="true"></i>
                          <span>Add Documentation</span>
                        </button>
                      </div>

                      @if (detailData.documentsAttached.length === 0) {
                        <p class="bond-empty-state">No attached documentation was found for this bond definition.</p>
                      } @else {
                        <div class="kt-table-wrapper kt-scrollable">
                          <table class="kt-table">
                            <thead>
                              <tr>
                                <th>Description</th>
                                <th>Type</th>
                                <th>Filename</th>
                                <th class="text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (document of detailData.documentsAttached; track trackDocument($index, document)) {
                                <tr>
                                  <td>
                                    <app-document-description-cell
                                      [description]="document.description"
                                      [documentLabel]="document.filename"
                                      [disabled]="isMutatingDocument()"
                                      [saving]="describingDocumentId() === document.id"
                                      (descriptionChange)="updateDocumentDescription(document, $event)"
                                    />
                                  </td>
                                  <td>{{ document.type || '-' }}</td>
                                  <td>
                                    <app-document-name-cell
                                      [filename]="document.filename"
                                      [link]="document.ezShareLink"
                                      [disabled]="isMutatingDocument()"
                                      [saving]="renamingDocumentId() === document.id"
                                      [siblingNames]="documentFilenames(detailData.documentsAttached)"
                                      (rename)="renameDocument(document, $event)"
                                    />
                                  </td>
                                  <td class="text-center">
                                    <span class="inline-flex items-center gap-2">
                                      <button
                                        type="button"
                                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                        [disabled]="isMutatingDocument()"
                                        data-kt-modal-toggle="#bond_definition_add_document_modal"
                                        (click)="startEditDocumentRow(document)"
                                        aria-label="Edit documentation row"
                                      >
                                        <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                      </button>
                                      <button
                                        type="button"
                                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                        data-kt-modal-toggle="#bond_definition_delete_document_modal"
                                        [disabled]="isMutatingDocument()"
                                        (click)="requestDeleteDocumentRow(document)"
                                        aria-label="Delete documentation row"
                                      >
                                        <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                      </button>
                                    </span>
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>
                  </section>
                }

                @if (isTabActive('notes')) {
                  <section class="bond-definition-tab-panel">
                    <div class="bond-tab-stack">
                      <div class="flex items-center justify-between gap-2">
                        <h2 class="text-base font-semibold">Notes</h2>
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          data-kt-modal-toggle="#bond_definition_add_note_modal"
                          (click)="resetAddNoteForm()"
                          aria-label="Add note"
                        >
                          <i class="ki-filled ki-plus" aria-hidden="true"></i>
                        </button>
                      </div>

                      @if (detailData.notes.length === 0) {
                        <p class="bond-empty-state">No notes were found for this bond definition.</p>
                      } @else {
                        <div class="kt-table-wrapper kt-scrollable">
                          <table class="kt-table">
                            <thead>
                              <tr>
                                <th>Note</th>
                                <th>Author</th>
                                <th>Date Modified</th>
                                <th class="text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (note of detailData.notes; track trackNote($index, note)) {
                                <tr>
                                  <td>{{ note.note || '-' }}</td>
                                  <td>{{ note.author || '-' }}</td>
                                  <td>{{ formatDateTime(note.createdAt) }}</td>
                                  <td class="text-center">
                                    <span class="inline-flex items-center gap-2">
                                      <button
                                        type="button"
                                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                        data-kt-modal-toggle="#bond_definition_add_note_modal"
                                        [disabled]="isMutatingNote()"
                                        (click)="startEditNoteRow(note)"
                                        aria-label="Edit note"
                                      >
                                        <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                      </button>
                                      <button
                                        type="button"
                                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                        [disabled]="isMutatingNote()"
                                        (click)="requestDeleteNoteRow(note)"
                                        aria-label="Delete note"
                                      >
                                        <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                      </button>
                                    </span>
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>
                  </section>
                }
              </div>
            </section>
          }
        }
      </div>
    </div>
  </div>
</main>

<app-catalog-crud-modal
  modalId="bond_definition_formulas_modal"
  title="Formulas"
  submitLabel=""
  cancelLabel="Close"
  [showSubmit]="false"
  [submitting]="false"
  [formError]="''"
  widthClass="max-w-[900px]"
>
  <div modal-body>
    @if ((detail()?.formulas?.length ?? 0) === 0) {
      <p class="bond-empty-state">No formulas available for this bond definition.</p>
    } @else {
      <div class="kt-table-wrapper kt-scrollable">
        <table class="kt-table">
          <thead>
            <tr>
              <th>Apply Date</th>
              <th>Formula</th>
            </tr>
          </thead>
          <tbody>
            @for (formula of detail()?.formulas ?? []; track trackFormula($index, formula)) {
              <tr>
                <td>{{ formatDateTime(formula.applyDate) }}</td>
                <td>{{ formula.formula || '-' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</app-catalog-crud-modal>

<app-catalog-crud-modal
  modalId="bond_definition_add_document_modal"
  [title]="documentModalTitle()"
  [submitLabel]="documentSubmitLabel()"
  [submitting]="isMutatingDocument()"
  [formError]="addFormError()"
  (submit)="addOrUpdateDocument()"
>
  <div modal-body>
    <div class="grid gap-3">
      @if (editingDocumentId() === null) {
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="bond-definition-document-files">Files</label>
          <input
            #documentFilesInput
            id="bond-definition-document-files"
            type="file"
            class="kt-input"
            accept=".doc,.msg,.pdf,.xls"
            multiple
            [disabled]="isMutatingDocument()"
            (change)="onDocumentFilesSelected($event)"
          />
          <p class="text-xs text-secondary-foreground">Allowed types: .doc, .msg, .pdf, .xls</p>
        </div>

        @if (documentUploadEntries().length > 0) {
          <div class="grid gap-1">
            <p class="text-sm font-medium">Selected files</p>
            <app-document-upload-list [entries]="documentUploadEntries()" />
          </div>
        }
      } @else {
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="bond-definition-document-filename">Filename</label>
          <input
            id="bond-definition-document-filename"
            type="text"
            class="kt-input"
            [value]="addDocumentForm().filename"
            (input)="updateAddDocumentField('filename', $any($event.target).value)"
          />
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="bond-definition-document-description">Description</label>
          <input
            id="bond-definition-document-description"
            type="text"
            class="kt-input"
            [value]="addDocumentForm().description"
            (input)="updateAddDocumentField('description', $any($event.target).value)"
          />
        </div>
      }
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_definition_delete_document_modal"
  title="Delete Document"
  [message]="'Delete document: ' + (pendingDocumentDeletePreview() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingDocument()"
  (confirm)="confirmDeleteDocumentRow()"
  (cancel)="cancelDeleteDocumentRow()"
></app-catalog-delete-confirm-modal>

<app-catalog-crud-modal
  modalId="bond_definition_add_note_modal"
  [title]="noteModalTitle()"
  [submitLabel]="noteSubmitLabel()"
  [submitting]="isMutatingNote()"
  [formError]="addFormError()"
  (submit)="addOrUpdateNote()"
  (cancel)="resetAddNoteForm()"
>
  <div modal-body>
    <div class="grid gap-1.5">
      <label class="kt-form-label" for="bond-definition-note-text">Note</label>
      <textarea
        id="bond-definition-note-text"
        class="kt-textarea"
        rows="4"
        [value]="addNoteForm().note"
        (input)="updateAddNoteField($any($event.target).value)"
      ></textarea>
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_definition_delete_note_modal"
  title="Delete Note"
  [message]="'Delete note: ' + (pendingNoteDeletePreview() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingNote()"
  (confirm)="confirmDeleteNoteRow()"
  (cancel)="cancelDeleteNoteRow()"
></app-catalog-delete-confirm-modal>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#bond_definition_formulas_modal"
></button>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#bond_definition_delete_note_modal"
></button>
